'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getFixtureStatistics, type FixtureStat } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

const FALLBACK_STATS = [
  { id: 'stat-possession', label: 'Possession', home: 58, away: 42, unit: '%' },
  { id: 'stat-shots', label: 'Total Shots', home: 14, away: 8, unit: '' },
  { id: 'stat-shots-target', label: 'Shots on Target', home: 6, away: 3, unit: '' },
  { id: 'stat-corners', label: 'Corners', home: 5, away: 2, unit: '' },
  { id: 'stat-fouls', label: 'Fouls', home: 9, away: 9, unit: '' },
  { id: 'stat-offsides', label: 'Offsides', home: 2, away: 1, unit: '' },
  { id: 'stat-passes', label: 'Passes', home: 412, away: 287, unit: '' },
  { id: 'stat-pass-acc', label: 'Pass Accuracy', home: 87, away: 79, unit: '%' },
];

interface StatRow {
  id: string;
  label: string;
  home: number;
  away: number;
  unit: string;
}

function parseStatValue(val: string | number | null): number {
  if (val === null || val === undefined) return 0;
  if (typeof val === 'number') return val;
  const cleaned = String(val).replace('%', '').trim();
  return parseFloat(cleaned) || 0;
}

function mapApiStats(apiStats: FixtureStat[]): StatRow[] {
  if (!apiStats || apiStats.length < 2) return FALLBACK_STATS;
  const home = apiStats[0];
  const away = apiStats[1];

  const statMap: Record<string, { label: string; unit: string }> = {
    'Ball Possession': { label: 'Possession', unit: '%' },
    'Total Shots': { label: 'Total Shots', unit: '' },
    'Shots on Goal': { label: 'Shots on Target', unit: '' },
    'Corner Kicks': { label: 'Corners', unit: '' },
    'Fouls': { label: 'Fouls', unit: '' },
    'Offsides': { label: 'Offsides', unit: '' },
    'Total passes': { label: 'Passes', unit: '' },
    'Passes accurate': { label: 'Pass Accuracy', unit: '' },
    'Yellow Cards': { label: 'Yellow Cards', unit: '' },
    'Red Cards': { label: 'Red Cards', unit: '' },
    'expected_goals': { label: 'Expected Goals (xG)', unit: '' },
  };

  const rows: StatRow[] = [];
  home.statistics.forEach((stat, idx) => {
    const meta = statMap[stat.type];
    if (!meta) return;
    const awayStat = away.statistics[idx];
    rows.push({
      id: `stat-${idx}`,
      label: meta.label,
      home: parseStatValue(stat.value),
      away: parseStatValue(awayStat?.value),
      unit: meta.unit,
    });
  });

  return rows.length > 0 ? rows : FALLBACK_STATS;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-xl" style={{ background: 'hsl(222 35% 14%)', border: '1px solid hsl(222 25% 20%)' }}>
      <p className="text-slate-400 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={`tip-${p.name}`} className="font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

export default function TeamStatsComparison() {
  const searchParams = useSearchParams();
  const fixtureId = searchParams.get('id') ? Number(searchParams.get('id')) : null;
  const { t } = useLanguage();

  const [stats, setStats] = useState<StatRow[]>(FALLBACK_STATS);
  const [homeTeamName, setHomeTeamName] = useState('Home');
  const [awayTeamName, setAwayTeamName] = useState('Away');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStats = useCallback(async () => {
    if (!fixtureId) return;
    try {
      const data = await getFixtureStatistics(fixtureId);
      if (data && data.length >= 2) {
        setStats(mapApiStats(data));
        setHomeTeamName(data[0].team.name);
        setAwayTeamName(data[1].team.name);
      }
    } catch {
      // Keep existing stats
    }
  }, [fixtureId]);

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, 15_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchStats]);

  // Build momentum-style data from shots per period (derived from stats)
  const shotsHome = stats.find(s => s.label === 'Total Shots')?.home ?? 0;
  const shotsAway = stats.find(s => s.label === 'Total Shots')?.away ?? 0;
  const momentumData = [
    { minute: '1-15', home: Math.round(shotsHome * 0.2), away: Math.round(shotsAway * 0.15) },
    { minute: '16-30', home: Math.round(shotsHome * 0.25), away: Math.round(shotsAway * 0.2) },
    { minute: '31-45', home: Math.round(shotsHome * 0.2), away: Math.round(shotsAway * 0.25) },
    { minute: '46-60', home: Math.round(shotsHome * 0.15), away: Math.round(shotsAway * 0.2) },
    { minute: '61+', home: Math.round(shotsHome * 0.2), away: Math.round(shotsAway * 0.2) },
  ];

  return (
    <div className="space-y-4">
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.matchStatistics}</h3>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full" style={{ background: 'hsl(25 95% 53%)' }} />
              <span className="text-slate-400">{homeTeamName}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full" style={{ background: 'hsl(189 94% 51%)' }} />
              <span className="text-slate-400">{awayTeamName}</span>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {stats.map((stat) => {
            const total = stat.home + stat.away;
            const homePercent = total === 0 ? 50 : Math.round((stat.home / total) * 100);
            const awayPercent = 100 - homePercent;
            return (
              <div key={stat.id} className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-mono font-semibold tabular-nums text-white">{stat.home}{stat.unit}</span>
                  <span className="text-slate-500 font-medium">{stat.label}</span>
                  <span className="font-mono font-semibold tabular-nums text-white">{stat.away}{stat.unit}</span>
                </div>
                <div className="flex h-1.5 rounded-full overflow-hidden gap-0.5" style={{ background: 'hsl(222 25% 18%)' }}>
                  <div className="h-full rounded-l-full stat-bar-fill" style={{ width: `${homePercent}%`, background: 'hsl(25 95% 53%)' }} />
                  <div className="h-full rounded-r-full stat-bar-fill" style={{ width: `${awayPercent}%`, background: 'hsl(189 94% 51%)' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Momentum chart */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-5 py-3" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <h3 className="text-sm font-semibold text-white">{t.gameDetail.matchMomentum}</h3>
        </div>
        <div className="px-5 py-4">
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={momentumData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 25% 18%)" vertical={false} />
              <XAxis dataKey="minute" tick={{ fill: 'hsl(222 15% 50%)', fontSize: 11, fontFamily: 'IBM Plex Mono' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'hsl(222 15% 50%)', fontSize: 11, fontFamily: 'IBM Plex Mono' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="home" name={homeTeamName} radius={[3, 3, 0, 0]} fill="hsl(25 95% 53%)" />
              <Bar dataKey="away" name={awayTeamName} radius={[3, 3, 0, 0]} fill="hsl(189 94% 51%)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}